import { DateTime } from "luxon";
import { TimePeriod, TimePeriodData } from "./time-period.class";
import cloneDeep from 'lodash/cloneDeep';


export interface HeaderData {
    updatedAt?: any;
    createdAt?: any;
    votesCounter?: number;
    ownerId?: string;
    ownerName?: string;
    pollId?: string;
    pollTemplate?: string;
    pollTitle?: string;
    pollDescription?: string;
    longDescription?: string;
    pollLongDescription?: string;
    limitPerUser?: boolean;
    limitPerOption?: number;
    allowToAddOptions?: boolean;
    getNotifications?: boolean;
    /** @deprecated free-text recipient is being retired — notifications go to the owner's auth email (concept 2026-09-01); field kept so legacy polls stay readable */
    notificationRecipient?: string | null;
    showAdvancedSettings?: boolean;
    showRegisterPeople?: boolean;
    showWhen?: boolean;
    showWhenAdvanced?: boolean;
    showLocationAdvanced?: boolean;
    showTimeZoneAdvanced?: boolean;
    showFormToUseAdvanced?: boolean;
    when?: TimePeriodData;
    /** @deprecated legacy toggle, no app sets it any more - the location shows by the template rule (headerLocation in persistence.utils) */
    showLocation?: boolean;
    location?: string;
    /**
     * Video call link (Zoom, Google Meet, Teams …), an https url of at most 500 characters, normalized by
     * normalizeMeetingLink (meeting-link.utils); shown right below the location, same templates and same place
     * in the editor as the location (calendar quick wins C, K1 = A). Not exported to BigQuery.
     * `null` = removed: HeaderService.persist() drops undefined keys, the firebase update deletes null ones.
     */
    meetingLink?: string | null;
    /** toggle of the meeting link in the advanced section - the counterpart of showLocationAdvanced */
    showMeetingLinkAdvanced?: boolean;
    colorPalette?: string;
    pollStyleKey?: string;
    backgroundSizeKey?: string;
    backgroundSizeKeyMobile?: string;
    backgroundSizeDifferentForMobile?: boolean;
    showRespondTill?: boolean;
    respondTill?: TimePeriodData; // startHour  and endHour not in use
    /** Poll manually closed by the owner. */
    closed?: boolean;
    /** Opt-in: auto-close once the respondTill day has passed (undefined = off). */
    autoCloseAfterRespondTill?: boolean;
    /**
     * Group switch for the "Will you join?" block (register-people) in all vote forms.
     * `undefined` = legacy header, the effective value is derived on load
     * (`HeaderService.normalizeLegacyHeader`). Subsettings: askForMultiplePersons → askForKids;
     * askForVegetarians (workorder-register-people-unification-2026-09-10).
     */
    registerPeople?: boolean;
    askForMultiplePersons?: boolean
    /**
     * Subsetting of registerPeople (sibling of askForMultiplePersons and askForVegetarians):
     * hard limit for the whole event, counted as the sum of adultsCount + kidsCount over all
     * votes with choiceInvitation === 1. Integer >= 1; missing = no limit (never persisted as 0).
     * Enforced client-side only, like the option limit; declining stays possible when full;
     * no auto-close (workorder-header-max-persons-2026-09-10, decisions 1-5).
     * `null` = explicit removal of a stored limit: HeaderService.persist() writes the header
     * with a firebase update, which leaves absent keys untouched but deletes keys set to null.
     */
    maxPersons?: number | null;
    showChooseTheme?: boolean;
    theme?: string,
    backgroundUrl?: string
    askForKids?: boolean;
    askForVegetarians?: boolean;
    askForEmailAdress?: boolean;
    /**
     * Subsetting of askForEmailAdress: every participant gets a copy of their response
     * mailed to the address they entered (concept-vote-confirmation-mail-2026-09-05).
     * Missing = off; the client keeps it false whenever askForEmailAdress is off.
     */
    sendVoteConfirmation?: boolean;
    /** Participant link slug: whocan.org/vote/{voteSlug} (concept-custom-participant-links-2026-10-28). Index whoo/pollSlugs/{slug} points back. */
    voteSlug?: string;
    /** Up to two previous slugs that still resolve (0 = most recent). Together with voteSlug the limit of 3 (E4). */
    voteSlugAliases?: { 0?: string; 1?: string };
    askForName?: boolean;
    askForPhoneNumber?: boolean;
    hideParticipants?: boolean;/**depricated */
    showParticipants?: boolean;
    askForPrivateComments?: boolean;
    askForPublicComments?: boolean;
    allowParticipantsNotifications?: boolean;
    mayBe?: boolean;
    timeZone?: string;
    formToUse?: string
    fontFamily?: string
    backgroundColor?: string
    advancedFontColors?: boolean
    fontColor1?: string
    fontColor2?: string
    fontColor3?: string
    /**
     * Font size of the poll header (generic header / invitation left side), preset key of
     * font-size.config.ts (xs|s|m|l|xl); missing = m (scale 1, renders exactly like before the
     * setting existed). `advancedFontSizes` switches to one key per element group; a missing group
     * key falls back to `fontSize` (workorder-font-size-setting-2026-09-14, 7 groups since Nachtrag 1).
     */
    fontSize?: string
    advancedFontSizes?: boolean
    fontSizeTitle?: string
    fontSizeSubtitle?: string
    fontSizeDescription?: string
    fontSizeWhen?: string
    fontSizeWhere?: string
    fontSizeRespondTill?: string
    /** activity box - only rendered by the invitation left side */
    fontSizeActivities?: string
    /**
     * true = header title/subtitle/boxes are rendered as typed instead of uppercase (customize-theme
     * dialog, panel "font family", workorder-header-uppercase-toggle-2026-09-15). Unset = uppercase (legacy).
     */
    disableUppercase?: boolean
    /** Sample-Polls: vom Admin publizierter Demo-Poll, indexable durch Suchmaschinen. */
    isSamplePoll?: boolean;
    /** URL-Slug fuer /poll-templates/{slug} — nur gesetzt wenn isSamplePoll=true. */
    slug?: string;
    /**
     * Sprache eines Sample-Polls. Nur fuer Sample-Polls relevant; bestimmt unter
     * welchem `samplePollSlugs/{slug}/{lang}`-Eintrag der Poll registriert ist und
     * unter welcher hreflang-Variante Suchmaschinen ihn finden.
     */
    pollLanguage?: SamplePollLanguage;

}

export type SamplePollLanguage = 'en' | 'de' | 'es' | 'fr' | 'it' | 'pt';
export const SAMPLE_POLL_LANGUAGES: SamplePollLanguage[] = ['en', 'de', 'es', 'fr', 'it', 'pt'];
/**
 * Upper bound of header.maxPersons. Mirrors the `.validate` rule on `whoo/header/$owner/$poll/maxPersons`
 * in `projects/whocan-app/database.rules.json` (`newData.val() <= 100000`) - a larger value is rejected by
 * the database as PERMISSION_DENIED, so the setter clamps before the write
 * (workorder-persist-new-poll-single-flight-2026-09-22, task 6). Change both places together.
 */
export const MAX_PERSONS_LIMIT = 100000;
export class HeaderClass {
    private _headerDataSinceLastPersistence: string;
    private _headerData: HeaderData = {};
    private _respondTill: TimePeriod;
    private _when: TimePeriod;
    /** the data of the "today 12:00" default the when getter created for a header without a date (see whenIsPlaceholder) */
    private _whenPlaceholderData: string;
    constructor(
        headerData: HeaderData,
        pollTemplate?: string,
        pollTitle?,
        themeKey?: string
    ) {
        if (headerData) this._headerData = cloneDeep(headerData)
        if (pollTemplate) this._headerData.pollTemplate = pollTemplate
        if (pollTitle) this._headerData.pollTitle = pollTitle
        if (themeKey) this._headerData.theme = themeKey
        this.setShowParticipantsDefault()
    }

    /**
     * used to transform old polls not  using attribute 'showParticipants' but 'showParticipants'
     */
    private setShowParticipantsDefault() {
        if (this._headerData.showParticipants === undefined) {
            if (this._headerData.hideParticipants === undefined) {
                this._headerData.showParticipants = true
            } else if (this._headerData.hideParticipants === false) {
                this._headerData.showParticipants = true
            } else if (this._headerData.hideParticipants === true) {
                this._headerData.showParticipants = true;
            }
        } else {

        }
    }

    /**
     * uninitialize() drops the data while the lazily created TimePeriod objects stay behind,
     * so without the first guard every read after a poll switch or logout would try to write
     * into null and throw a TypeError - e.g. the auto-save of the vote preview that is still
     * in flight (workorder-header-persist-permission-denied-2026-09-12).
     */
    get headerData(): HeaderData {
        if (!this._headerData) return null;
        if (this._when) this._headerData.when = this._when.timePeriodData;
        if (this._respondTill) this._headerData.respondTill = this._respondTill.timePeriodData;
        return this._headerData
    }

    get updatedAt(): any {
        return this._headerData?.updatedAt;
    }
    set updatedAt(value: any) {
        this._headerData.updatedAt = value;
    }
    get createdAt(): any {
        return this._headerData?.createdAt;
    }
    set createdAt(value: any) {
        this._headerData.createdAt = value;
    }
    get votesCounter(): number {
        if (this._headerData?.votesCounter) {
            return this._headerData.votesCounter;
        } else {
            return 0
        }
    }
    set votesCounter(value: number) {
        this._headerData.votesCounter = value;
    }
    get ownerId(): string {
        return this._headerData?.ownerId;
    }
    set ownerId(value: string) {
        this._headerData.ownerId = value;
    }
    get ownerName(): string {
        return this._headerData?.ownerName;
    }
    set ownerName(value: string) {
        this._headerData.ownerName = value;
    }
    get pollId(): string {
        return this._headerData?.pollId;
    }
    set pollId(value: string) {
        this._headerData.pollId = value;
    }
    get pollTemplate(): string {
        return this._headerData?.pollTemplate;
    }
    set pollTemplate(value: string) {
        this._headerData.pollTemplate = value;
    }
    get pollTitle(): string {
        return this._headerData?.pollTitle;
    }
    set pollTitle(value: string) {
        this._headerData.pollTitle = value;
    }
    get pollDescription(): string {
        return this._headerData?.pollDescription;
    }
    set pollDescription(value: string) {
        this._headerData.pollDescription = value;
    }
    get longDescription(): string {
        return this._headerData?.longDescription;
    }
    set longDescription(value: string) {
        this._headerData.longDescription = value;
    }
    get pollLongDescription(): string {
        return this._headerData?.pollLongDescription;
    }
    set pollLongDescription(value: string) {
        this._headerData.pollLongDescription = value;
    }
    get limitPerUser(): boolean {
        return this._headerData?.limitPerUser;
    }

    set limitPerUser(value: boolean) {
        this._headerData.limitPerUser = value;
    }

    get limitPerOption(): number {
        return this._headerData?.limitPerOption;
    }

    set limitPerOption(value: number) {
        this._headerData.limitPerOption = value;
    }

    /**@deprecated */
    get hideParticipants(): boolean {
        return this._headerData?.hideParticipants;
    }

    /**@deprecated */
    set hideParticipants(value: boolean) {
        this._headerData.hideParticipants = value;
    }

    get showParticipants(): boolean {
        return this._headerData?.showParticipants;
    }
    set showParticipants(value: boolean) {
        this._headerData.showParticipants = value;
    }

    get allowParticipantsNotifications(): boolean {
        if (!this.showParticipants) return false;
        return this._headerData?.allowParticipantsNotifications;
    }
    set allowParticipantsNotifications(value: boolean) {
        this._headerData.allowParticipantsNotifications = value;
    }

    get askForPublicComments(): boolean {
        if (!this.showParticipants) return false;
        return this._headerData?.askForPublicComments;
    }
    set askForPublicComments(value: boolean) {
        this._headerData.askForPublicComments = value;
    }

    get allowToAddOptions(): boolean {
        return this._headerData?.allowToAddOptions;
    }

    set allowToAddOptions(value: boolean) {
        this._headerData.allowToAddOptions = value;
    }

    get getNotifications(): boolean {
        return this._headerData?.getNotifications;
    }

    set getNotifications(value: boolean) {
        this._headerData.getNotifications = value;
    }

    /**
     * @deprecated see HeaderData.notificationRecipient - read only for legacy polls until
     * scripts/strip-notification-recipient.mjs has run; the setter is gone since rules-hardening-1
     */
    get notificationRecipient(): string | null {
        return this._headerData?.notificationRecipient;
    }

    get showAdvancedSettings(): boolean {
        return this._headerData?.showAdvancedSettings;
    }

    set showAdvancedSettings(value: boolean) {
        this._headerData.showAdvancedSettings = value;
    }

    get showRegisterPeople(): boolean {
        return this._headerData?.showRegisterPeople;
    }

    set showRegisterPeople(value: boolean) {
        this._headerData.showRegisterPeople = value;
    }

    get showWhen(): boolean {
        return this._headerData?.showWhen;
    }

    set showWhen(value: boolean) {
        this._headerData.showWhen = value;
    }

    get showWhenAdvanced(): boolean {
        return this._headerData?.showWhenAdvanced;
    }

    set showWhenAdvanced(value: boolean) {
        this._headerData.showWhenAdvanced = value;
    }

    get showLocationAdvanced(): boolean {
        return this._headerData?.showLocationAdvanced;
    }

    set showLocationAdvanced(value: boolean) {
        this._headerData.showLocationAdvanced = value;
    }

    get showTimeZoneAdvanced(): boolean {
        return this._headerData?.showTimeZoneAdvanced;
    }
    set showTimeZoneAdvanced(value: boolean) {
        this._headerData.showTimeZoneAdvanced = value;
    }
    get showFormToUseAdvanced(): boolean {
        return this._headerData?.showFormToUseAdvanced;
    }
    set showFormToUseAdvanced(value: boolean) {
        this._headerData.showFormToUseAdvanced = value;
    }

    get when(): TimePeriod {
        if (!this._when) {
            const stored = this._headerData?.when as { startString?: string, date?: string };
            this._when = TimePeriod.newFromTimePeriodData(this._headerData?.when);
            this._whenPlaceholderData = stored?.startString || stored?.date ? undefined : JSON.stringify(this._when.timePeriodData);
        }
        return this._when
    }

    /**
     * true while `when` is the "today 12:00" default the getter created for a header without a date and nobody
     * changed it. headerData writes that default back into the data, so whoever needs to know whether the
     * header has a date (the calendar export, workorder-calendar-quick-wins-a-2026-11-02) asks here.
     */
    get whenIsPlaceholder(): boolean {
        if (!this._when || this._whenPlaceholderData === undefined) return false;
        return JSON.stringify(this._when.timePeriodData) === this._whenPlaceholderData;
    }

    set when(value: TimePeriod) {
        this._whenPlaceholderData = undefined
        if (!this._when) {
            // Initialize with a clone of the input value
            this._when = value.clone();
        } else {
            // Update the existing object's properties
            this._when.copy(value);
        }
    }

    get respondTill(): TimePeriod {
        if (!this._respondTill) {
            this._respondTill = TimePeriod.newFromTimePeriodData(this._headerData?.respondTill);
        }
        return this._respondTill
    }

    set respondTill(value: TimePeriod) {
        if (!this._respondTill) {
            // Initialize with a clone of the input value
            this._respondTill = value.clone();
        } else {
            // Update the existing object's properties
            this._respondTill.copy(value);
        }
    }

    get showLocation(): boolean {
        return this._headerData?.showLocation;
    }

    set showLocation(value: boolean) {
        this._headerData.showLocation = value;
    }

    get formToUse(): string {
        return this._headerData?.formToUse;
    }
    set formToUse(value: string) {
        this._headerData.formToUse = value;
    }
    get location(): string {
        return this._headerData?.location;
    }
    set location(value: string) {
        this._headerData.location = value;
    }
    get meetingLink(): string | null {
        return this._headerData?.meetingLink;
    }
    set meetingLink(value: string | null) {
        this._headerData.meetingLink = value;
    }
    get showMeetingLinkAdvanced(): boolean {
        return this._headerData?.showMeetingLinkAdvanced;
    }
    set showMeetingLinkAdvanced(value: boolean) {
        this._headerData.showMeetingLinkAdvanced = value;
    }
    get colorPalette(): string {
        return this._headerData?.colorPalette;
    }
    set colorPalette(value: string) {
        this._headerData.colorPalette = value;
    }
    get pollStyleKey(): string {
        return this._headerData?.pollStyleKey;
    }
    set pollStyleKey(value: string) {
        this._headerData.pollStyleKey = value;
    }
    get backgroundSizeKey(): string {
        return this._headerData?.backgroundSizeKey;
    }
    set backgroundSizeKey(value: string) {
        this._headerData.backgroundSizeKey = value;
    }
    get backgroundSizeKeyMobile(): string {
        return this._headerData?.backgroundSizeKeyMobile;
    }
    set backgroundSizeKeyMobile(value: string) {
        this._headerData.backgroundSizeKeyMobile = value;
    }
    get backgroundSizeDifferentForMobile(): boolean {
        return this._headerData?.backgroundSizeDifferentForMobile;
    }
    set backgroundSizeDifferentForMobile(value: boolean) {
        this._headerData.backgroundSizeDifferentForMobile = value;
    }
    get showRespondTill(): boolean {
        return this._headerData?.showRespondTill;
    }
    set showRespondTill(value: boolean) {
        this._headerData.showRespondTill = value;
        // hiding the deadline also disables auto-close — otherwise an invisible
        // deadline could still close the poll (setter is only called from the UI,
        // the constructor deserializes directly, so persisted data stays untouched)
        if (!value) this._headerData.autoCloseAfterRespondTill = false;
    }
    get closed(): boolean {
        return this._headerData?.closed;
    }
    set closed(value: boolean) {
        this._headerData.closed = value;
    }
    get autoCloseAfterRespondTill(): boolean {
        return this._headerData?.autoCloseAfterRespondTill;
    }
    set autoCloseAfterRespondTill(value: boolean) {
        this._headerData.autoCloseAfterRespondTill = value;
    }
    /**
     * End of the respondTill day in the poll's time zone.
     *
     * The deadline is stored as a zone-less ISO string (TimePeriodPersistanceData.startString,
     * legacy polls: `date`). We interpret it in header.timeZone and close at endOf('day'),
     * so the poll stays open through the whole deadline day, not just until the stored
     * clock time (spec decision, workorder 2026-07-25 §1).
     *
     * Reads the raw header data on purpose: the respondTill/timeZone getters lazily
     * create default values and would turn "no deadline set" into "deadline today".
     */
    get respondTillEndOfDay(): DateTime | undefined {
        const raw = this._headerData?.respondTill as { startString?: string; date?: string } | undefined;
        const startString = raw?.startString || raw?.date;
        if (!startString) return undefined;
        const zone = this._headerData?.timeZone || DateTime.local().zoneName;
        const start = DateTime.fromISO(startString, { zone });
        return start.isValid ? start.endOf('day') : undefined;
    }
    get isRespondTillExpired(): boolean {
        const endOfDay = this.respondTillEndOfDay;
        return !!endOfDay && DateTime.now() > endOfDay;
    }
    /**
     * Closed = manually closed by the owner, or auto-close opted in and the respondTill
     * deadline has passed. Evaluated client-side only (no cron / cloud function).
     * Existing polls (both fields undefined) always count as open, even with a
     * respondTill date in the past.
     */
    get isClosed(): boolean {
        if (this._headerData?.closed === true) return true;
        if (this._headerData?.autoCloseAfterRespondTill === true) return this.isRespondTillExpired;
        return false;
    }

    get askForMultiplePersons(): boolean {
        return this._headerData?.askForMultiplePersons;
    }
    set askForMultiplePersons(value: boolean) {
        this._headerData.askForMultiplePersons = value;
    }
    get showChooseTheme(): boolean {
        return this._headerData?.showChooseTheme;
    }
    set showChooseTheme(value: boolean) {
        this._headerData.showChooseTheme = value;
    }
    get theme(): string {
        return this._headerData?.theme;
    }
    set theme(value: string) {
        this._headerData.theme = value;
    }
    get backgroundUrl(): string {
        return this._headerData?.backgroundUrl;
    }
    set backgroundUrl(value: string) {
        this._headerData.backgroundUrl = value;
    }
    get registerPeople(): boolean {
        return this._headerData?.registerPeople;
    }
    set registerPeople(value: boolean) {
        this._headerData.registerPeople = value;
    }
    /** the stored limit, undefined when there is none (a pending removal reads as none too) */
    get maxPersons(): number | undefined {
        return this._headerData?.maxPersons ?? undefined;
    }
    /**
     * Normalizes on the way in: anything that is not a finite integer >= 1 removes the limit
     * (the editor toggle "off" and an emptied number field both end up here). Fractions are
     * rounded down — the rules only accept whole numbers; anything above MAX_PERSONS_LIMIT is capped
     * to it, the upper bound of the database rule. A removal is stored as null when a value was
     * present (so the next persist deletes the key in the database, see HeaderData); a header that
     * never had a limit stays without the field.
     */
    set maxPersons(value: number | undefined) {
        const normalized = typeof value === 'number' && Number.isFinite(value) ? Math.floor(value) : NaN;
        if (Number.isNaN(normalized) || normalized < 1) {
            if (this._headerData.maxPersons !== undefined) this._headerData.maxPersons = null;
        } else {
            this._headerData.maxPersons = Math.min(normalized, MAX_PERSONS_LIMIT);
        }
    }
    get askForKids(): boolean {
        return this._headerData?.askForKids;
    }
    set askForKids(value: boolean) {
        this._headerData.askForKids = value;
    }
    get askForVegetarians(): boolean {
        return this._headerData?.askForVegetarians;
    }
    set askForVegetarians(value: boolean) {
        this._headerData.askForVegetarians = value;
    }
    get askForEmailAdress(): boolean {
        return this._headerData?.askForEmailAdress;
    }
    set askForEmailAdress(value: boolean) {
        this._headerData.askForEmailAdress = value;
    }
    get sendVoteConfirmation(): boolean {
        return this._headerData?.sendVoteConfirmation;
    }
    set sendVoteConfirmation(value: boolean) {
        this._headerData.sendVoteConfirmation = value;
    }
    get voteSlug(): string | undefined {
        return this._headerData?.voteSlug;
    }
    set voteSlug(value: string | undefined) {
        this._headerData.voteSlug = value;
    }
    get voteSlugAliases(): { 0?: string; 1?: string } | undefined {
        return this._headerData?.voteSlugAliases;
    }
    set voteSlugAliases(value: { 0?: string; 1?: string } | undefined) {
        this._headerData.voteSlugAliases = value;
    }
    get askForName(): boolean {
        if (this._headerData?.askForName === undefined) {
            return true;
        }
        return this._headerData.askForName;
    }
    set askForName(value: boolean) {
        this._headerData.askForName = value;
    }
    get askForPhoneNumber(): boolean {
        return this._headerData?.askForPhoneNumber;
    }
    set askForPhoneNumber(value: boolean) {
        this._headerData.askForPhoneNumber = value;
    }
    get askForPrivateComments(): boolean {
        return this._headerData?.askForPrivateComments;
    }
    set askForPrivateComments(value: boolean) {
        this._headerData.askForPrivateComments = value;
    }
    get timeZone(): string {
        if (!this._headerData) { return DateTime.local().zoneName }
        if (!this._headerData.timeZone) {
            this._headerData.timeZone = DateTime.local().zoneName
        }
        return this._headerData?.timeZone;
    }
    set timeZone(value: string) {
        this._headerData.timeZone = value;
    }

    get fontFamily(): string {
        return this._headerData?.fontFamily;
    }
    set fontFamily(value: string) {
        this._headerData.fontFamily = value;
    }

    get backgroundColor(): string {
        return this._headerData?.backgroundColor;
    }
    set backgroundColor(value: string) {
        this._headerData.backgroundColor = value;
    }

    get advancedFontColors(): boolean {
        return this._headerData?.advancedFontColors;
    }
    set advancedFontColors(value: boolean) {
        this._headerData.advancedFontColors = value;
    }

    get fontColor1(): string {
        return this._headerData?.fontColor1;
    }
    set fontColor1(value: string) {
        this._headerData.fontColor1 = value;
    }

    get fontColor2(): string {
        return this._headerData?.fontColor2;
    }
    set fontColor2(value: string) {
        this._headerData.fontColor2 = value;
    }

    get fontColor3(): string {
        return this._headerData?.fontColor3;
    }
    set fontColor3(value: string) {
        this._headerData.fontColor3 = value;
    }

    get fontSize(): string {
        return this._headerData?.fontSize;
    }
    set fontSize(value: string) {
        this._headerData.fontSize = value;
    }

    get advancedFontSizes(): boolean {
        return this._headerData?.advancedFontSizes;
    }
    set advancedFontSizes(value: boolean) {
        this._headerData.advancedFontSizes = value;
    }

    get fontSizeTitle(): string {
        return this._headerData?.fontSizeTitle;
    }
    set fontSizeTitle(value: string) {
        this._headerData.fontSizeTitle = value;
    }

    get fontSizeSubtitle(): string {
        return this._headerData?.fontSizeSubtitle;
    }
    set fontSizeSubtitle(value: string) {
        this._headerData.fontSizeSubtitle = value;
    }

    get fontSizeDescription(): string {
        return this._headerData?.fontSizeDescription;
    }
    set fontSizeDescription(value: string) {
        this._headerData.fontSizeDescription = value;
    }

    get fontSizeWhen(): string {
        return this._headerData?.fontSizeWhen;
    }
    set fontSizeWhen(value: string) {
        this._headerData.fontSizeWhen = value;
    }

    get fontSizeWhere(): string {
        return this._headerData?.fontSizeWhere;
    }
    set fontSizeWhere(value: string) {
        this._headerData.fontSizeWhere = value;
    }

    get fontSizeRespondTill(): string {
        return this._headerData?.fontSizeRespondTill;
    }
    set fontSizeRespondTill(value: string) {
        this._headerData.fontSizeRespondTill = value;
    }

    get fontSizeActivities(): string {
        return this._headerData?.fontSizeActivities;
    }
    set fontSizeActivities(value: string) {
        this._headerData.fontSizeActivities = value;
    }

    get disableUppercase(): boolean {
        return this._headerData?.disableUppercase;
    }
    set disableUppercase(value: boolean) {
        this._headerData.disableUppercase = value;
    }

    /** UI helper for the positive checkbox "uppercase": inverse of disableUppercase, unset = true */
    get uppercase(): boolean {
        return !this._headerData?.disableUppercase;
    }
    set uppercase(value: boolean) {
        this._headerData.disableUppercase = !value;
    }

    get mayBe(): boolean {
        return this._headerData?.mayBe;
    }
    set mayBe(value: boolean) {
        this._headerData.mayBe = value;
    }

    get isSamplePoll(): boolean {
        return this._headerData?.isSamplePoll === true;
    }
    set isSamplePoll(value: boolean) {
        this._headerData.isSamplePoll = value;
    }

    get slug(): string {
        return this._headerData?.slug;
    }
    set slug(value: string) {
        this._headerData.slug = value;
    }

    get pollLanguage(): SamplePollLanguage {
        return this._headerData?.pollLanguage;
    }
    set pollLanguage(value: SamplePollLanguage) {
        this._headerData.pollLanguage = value;
    }

    public uninitialize() {
        this._headerData = null
        // the lazy time fields must not outlive the data they were derived from
        this._when = undefined
        this._whenPlaceholderData = undefined
        this._respondTill = undefined
    }

    get hasDescription() {
        if (this._headerData?.pollDescription && this._headerData?.pollDescription.length > 2) return true;
        return false
    }

    get hasLongDescription() {
        if (this._headerData?.longDescription && this._headerData?.longDescription.length > 2) return true;
        return false
    }

    public resetDataSinceLastPersistence() {
        this._headerDataSinceLastPersistence = JSON.parse(JSON.stringify(this.headerData))
    }

    public hasChangedSinceLastPersitance(): boolean {
        return (JSON.stringify(this.headerData) !== JSON.stringify(this._headerDataSinceLastPersistence))
    }


}
